/**
 * The sign-in step: the checkout's first step for a guest. What signing in with
 * Telegram brings, the Sign in with Telegram button, and where to get Telegram
 * for a visitor without it. The studio's name is said once.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'sign-in-step',
  description:
    "The checkout's sign-in step: what arrives in Telegram from the studio, Sign in with Telegram, and where to get Telegram, then a welcome back when the visitor returns.",
  drawing: 'standard-only',
  options: {},
  parts: {
    step: { description: 'The whole sign-in step.', required: false },
    lead: { description: "What arrives in Telegram from the studio once signed in.", required: false },
    signIn: { description: 'The Sign in with Telegram button.', required: true, states: ['disabled'], role: 'primary-action' },
    testAccount: { description: 'Development only: sign in as the test account.', required: false },
    error: { description: 'Shown when a sign-in did not go through.', required: false },
    noTelegram: { description: 'Where to get Telegram, for a visitor without it.', required: false },
    appStore: { description: 'Telegram in the App Store; opens in a new tab.', required: false },
    googlePlay: { description: 'Telegram in Google Play; opens in a new tab.', required: false },
    welcomeBack: { description: 'Shown when a visitor comes back from getting Telegram: their pick is still here.', required: false },
  },
  actions: {
    signIn: { description: "Opens Telegram's sign-in window; the checkout continues once signed in.", args: [] },
    signInAsTestAccount: { description: 'Development only: signs in as the test account.', args: [] },
    leftToGetTelegram: { description: 'Records that the visitor went to get Telegram, so their return is welcomed.', args: [{ name: 'store', kind: 'value', type: 'string', values: ['appStore', 'googlePlay'] }] },
  },
  events: {
    signInPressed: 'A visitor pressed Sign in with Telegram.',
  },
});

/** Where to get Telegram. */
export type TelegramStore = 'appStore' | 'googlePlay';

/** What the sign-in step knows. */
export interface SignInStepState {
  /** The studio's name, as the lead says it. */
  readonly studioName: string;
  /** True once Sign in can open Telegram's window (its one-time value and library are ready). */
  readonly ready: boolean;
  /** The last sign-in attempt: idle, pending, error (it did not go through) or unavailable (the studio has no bot yet). */
  readonly attempt: 'idle' | 'pending' | 'error' | 'unavailable';
  /** Development only: true when this server offers the test-account sign-in. */
  readonly testAccount: boolean;
  /** Telegram's page in each store, opened in a new tab. */
  readonly storeLinks: { readonly appStore: string; readonly googlePlay: string };
  /** True after the visitor came back from getting Telegram: their pick is still here. */
  readonly welcomeBack: boolean;
}

/** What a drawing can ask the sign-in step to do. */
export interface SignInStepActions {
  /** Sign in with Telegram; the checkout continues signed in. */
  readonly signIn: () => void;
  /** Development only: sign in as the test account; null when the server has none. */
  readonly signInAsTestAccount: (() => void) | null;
  /** Call as a store link opens, so the visitor's return is welcomed. */
  readonly leftToGetTelegram: (store: TelegramStore) => void;
}

/** What the sign-in step tells its host. */
export interface SignInStepEvents {
  /** A visitor pressed Sign in with Telegram. */
  readonly signInPressed: Record<string, never>;
}
